import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import {
  useCritiqueGroup,
  useCritiqueJoinRequests,
} from 'src/hooks/useCritiqueGroups';
import {
  activeMembers,
  canEditSettings,
  canManageGroup,
  groupSpineCoverUrl,
  isHost as isHostOf,
  nextCheckpoint,
  visibilityLabel,
} from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import {
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';
import { DiscussionWall } from 'src/components/critiqueGroups/discussionWall';
import { formatCheckpointDate } from 'src/components/critiqueGroups/checkpointTimeline';
import HostOverview from './hostOverview';
import HostMembers from './hostMembers';
import HostCheckpoints from './hostCheckpoints';
import HostSettings from './hostSettings';
import CritiqueGroupPreview from './critiqueGroupPreview';
import MemberHome from './memberHome';
import MemberFeedback from './memberFeedback';

/**
 * The group hub. One route serves both roles — the host gets the dashboard
 * (design C1) and a member gets their group home (F1) — because they're the
 * same group, seen from two seats. Role comes from the group payload
 * (`createdBy` + the caller's membership), never from a client guess.
 */

const Banner = styled.div`
  padding: 24px 32px;
  background: linear-gradient(120deg, #16413e, #0e302d);
  color: #eaf6f4;
  display: flex;
  gap: 20px;
  align-items: center;
  border-radius: 16px 16px 0 0;
  ${MediaQuery.phone} {
    padding: 18px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Stat = styled.div`
  div:first-child {
    font-size: 22px;
    font-weight: 800;
    color: #fff;
  }
  div:last-child {
    font-size: 11px;
    color: #9dc4bf;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 0 24px;
  background: var(--br-surface);
  border-bottom: 1px solid #eee7da;
  overflow-x: auto;
  /* overflow-x alone does NOT stop the strip widening the page: it caps the
     automatic minimum size of a flex/grid ITEM, but this strip is a block in a
     block chain, so its min-content (5 nowrap tabs, ~473px) propagated up to
     the route's min-content-sized grid item and dragged the whole page to 536px
     at a 375px viewport. Size containment keeps the strip's own width out of
     that calculation, so it fills the viewport and scrolls its overflow. */
  contain: inline-size;
  /* build.target is safari13 (BRNative embeds this site in old WebViews) but
     contain: inline-size only landed in Safari 16, where it would silently
     no-op and bring the overflow straight back. Wrapping to a second row is the
     graceful degradation: it drops the strip's min-content to its widest single
     tab, so the page still fits — just without the one-line rail. */
  @supports not (contain: inline-size) {
    flex-wrap: wrap;
  }
  /* The strip is a nav rail, not content: a scrollbar under 5 tabs reads as
     chrome. Hidden here, and the fade below is what signals it scrolls. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  /* Scroll-shadow: the two solid-white layers are attached to the content
     (local) so they slide away at each end, uncovering the fixed (scroll) fade
     underneath. Net effect — a fade appears on the right only while there is
     more to scroll to, with no JS and no extra DOM. */
  background-image: linear-gradient(to right, #fff 70%, rgba(255, 255, 255, 0)),
    linear-gradient(to left, #fff 70%, rgba(255, 255, 255, 0)),
    linear-gradient(to right, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0)),
    linear-gradient(to left, rgba(0, 0, 0, 0.09), rgba(0, 0, 0, 0));
  background-position: left center, right center, left center, right center;
  background-repeat: no-repeat;
  background-size: 28px 100%, 28px 100%, 12px 100%, 12px 100%;
  background-attachment: local, local, scroll, scroll;
  ${MediaQuery.phone} {
    padding: 0 10px;
    /* The rail above is a desktop win and a phone trap. Measured at a 375px
       viewport: the strip wants 432px, so the LAST tab — Settings, which is the
       only route to the cover upload — sat 70% off the right edge, behind a
       scrollbar this component deliberately hides. The fade is the only hint it
       scrolls, and it reads as a border. A host looking for their cover simply
       could not see where to go.
       Wrapping to a second row is the same degradation the @supports rule above
       already accepts, and here it's strictly better than a rail: 5 tabs cost
       one extra row, and every tab is visible and tappable without discovering
       a hidden gesture. Desktop keeps the one-line rail. */
    flex-wrap: wrap;
    /* The fades signal "there is more to scroll to" — there isn't now, and left
       as-is the right one reads as a shadow under a wrapped row. */
    background-image: none;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 14px;
  font-size: 13.5px;
  font-family: inherit;
  white-space: nowrap;
  ${MediaQuery.phone} {
    /* 5 tabs × 28px of padding is 140px of a 375px viewport; trimming it means
       fewer tabs sit off-screen before the user has to scroll. */
    padding: 13px 9px;
  }
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  color: ${({ $active }) => ($active ? '#00817b' : '#8a857b')};
  border-bottom: 3px solid ${({ $active }) => ($active ? '#00b5ad' : 'transparent')};
  &:hover {
    color: #00817b;
  }
`;

const Badge = styled.span`
  background: #ef7d22;
  color: #fff;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 999px;
  margin-left: 4px;
`;

const Content = styled.div`
  padding: 24px;
  background: var(--br-raised-surface);
  border: 1px solid #e2ddd0;
  border-top: none;
  border-radius: 0 0 16px 16px;
  ${MediaQuery.phone} {
    padding: 14px;
  }
`;

type TabKey =
  | 'overview'
  | 'members'
  | 'checkpoints'
  | 'discussion'
  | 'feedback'
  | 'settings';

const CritiqueGroupDetail: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams<{ groupId: string }>();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const uid = userProfile?._id;

  const { data: group, isLoading, isError, error, refetch } = useCritiqueGroup(groupId);

  const host = isHostOf(group, uid);
  const manages = canManageGroup(group, uid);

  // Only host/moderators may read requests (the API 403s anyone else), so this
  // stays unmounted for members rather than firing a doomed call.
  const requests = useCritiqueJoinRequests(groupId, { enabled: manages && !!group });
  const pendingCount = requests.data?.length ?? 0;

  // 'overview' is the first tab for both roles — the host dashboard, or the
  // member's group home.
  const [tab, setTab] = useState<TabKey>('overview');

  if (isLoading) return <CritiqueLoadingState />;
  if (isError || !group) {
    // A 403 means "not a member". If the group is public (`request`/`listed`)
    // the server will serve a metadata preview instead — show that, so a
    // non-member landing on the link gets the "Request to join" surface rather
    // than a dead end. A genuine 404 or a real fault still falls through.
    if (error?.status === 403) {
      return <CritiqueGroupPreview />;
    }
    return (
      <CritiqueErrorState
        message={
          error?.status === 404
            ? t('CritiqueNoAccess', 'This group isn’t available to you.')
            : error?.message
        }
        onRetry={() => refetch()}
      />
    );
  }

  const members = activeMembers(group);
  const next = nextCheckpoint(group.checkpoints || []);
  const firstBook = (group.books || [])[0];
  const bookTitle = typeof firstBook === 'string' ? undefined : firstBook?.title;

  const hostTabs: Array<{ key: TabKey; label: string; badge?: number }> = [
    { key: 'overview', label: t('CritiqueTabOverview', 'Overview') },
    { key: 'members', label: t('CritiqueTabMembers', 'Members'), badge: pendingCount },
    { key: 'checkpoints', label: t('CritiqueTabCheckpoints', 'Checkpoints') },
    { key: 'discussion', label: t('CritiqueTabDiscussion', 'Discussion') },
    { key: 'settings', label: t('CritiqueTabSettings', 'Settings') },
  ];

  const memberTabs: Array<{ key: TabKey; label: string; badge?: number }> = [
    { key: 'overview', label: t('CritiqueTabHome', 'Home') },
    { key: 'checkpoints', label: t('CritiqueTabCheckpoints', 'Checkpoints') },
    { key: 'discussion', label: t('CritiqueTabDiscussion', 'Discussion') },
    { key: 'feedback', label: t('CritiqueTabFeedback', 'Feedback') },
  ];

  const tabs = host ? hostTabs : memberTabs;
  const activeTab = tabs.some(x => x.key === tab) ? tab : tabs[0].key;

  return (
    <div data-testid="critique-group-detail">
      <Banner>
        <BookSpine
          title={bookTitle}
          coverUrl={groupSpineCoverUrl(group)}
          seed={group._id}
          width={62}
          height={88}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                color: '#fff',
                // A group name is free text; a long unbroken one would otherwise
                // set the page's min-content width. wordBreak is the Safari
                // < 15.4 stand-in (see components/critiqueGroups/memberName.tsx).
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {group.name}
            </h3>
            <span
              data-testid="critique-visibility-badge"
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: 'rgba(255,255,255,.16)',
                padding: '4px 10px',
                borderRadius: 999,
              }}
            >
              <Icon
                name={visibilityLabel(group.visibility, t).icon as any}
                style={{ marginRight: 4 }}
              />
              {visibilityLabel(group.visibility, t).label}
            </span>
          </div>
          <p style={{ margin: '5px 0 0', fontSize: 13.5, color: '#b8d6d2' }}>
            {bookTitle || t('CritiqueNoBookLinked', 'No book linked yet')}
          </p>
          <div style={{ display: 'flex', gap: 22, marginTop: 14, flexWrap: 'wrap' }}>
            <Stat>
              <div>{members.length}</div>
              <div>{t('CritiqueMembers', 'Members')}</div>
            </Stat>
            <Stat>
              <div>{group.checkpoints?.length ?? 0}</div>
              <div>{t('CritiqueCheckpoints', 'Checkpoints')}</div>
            </Stat>
            <Stat>
              <div>{next ? formatCheckpointDate(next.dueAt) : '—'}</div>
              <div>{t('CritiqueNextCheckpoint', 'Next checkpoint')}</div>
            </Stat>
          </div>
        </div>
      </Banner>

      <Tabs role="tablist">
        {tabs.map(x => (
          <TabButton
            key={x.key}
            role="tab"
            aria-selected={activeTab === x.key}
            $active={activeTab === x.key}
            onClick={() => setTab(x.key)}
            data-testid={`critique-tab-${x.key}`}
          >
            {x.label}
            {!!x.badge && x.badge > 0 && <Badge>{x.badge}</Badge>}
          </TabButton>
        ))}
      </Tabs>

      <Content>
        {activeTab === 'overview' &&
          (host ? (
            <HostOverview group={group} requests={requests} onOpenTab={setTab} />
          ) : (
            <MemberHome group={group} onOpenTab={setTab} />
          ))}
        {activeTab === 'members' && host && (
          <HostMembers group={group} requests={requests} />
        )}
        {activeTab === 'checkpoints' &&
          (host ? (
            <HostCheckpoints group={group} />
          ) : (
            <MemberHome group={group} onOpenTab={setTab} checkpointsOnly />
          ))}
        {activeTab === 'discussion' && (
          <DiscussionWall
            groupId={group._id}
            canPin={manages}
            currentUser={userProfile}
          />
        )}
        {activeTab === 'feedback' && !host && <MemberFeedback group={group} />}
        {activeTab === 'settings' && canEditSettings(group, uid) && (
          <HostSettings group={group} />
        )}
      </Content>
    </div>
  );
};

export default CritiqueGroupDetail;
